import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ScreenSkeleton } from '../../components/atoms/ScreenSkeleton';
import { CircleUserRound, X } from 'lucide-react';
import { useParams } from 'react-router';
import { useAtom } from 'jotai';
import {
  inspectionHistoryParamsAtom,
  inspectionHistoryAtom,
} from '../../store/inspection/observation/actionTypeStore';
import ColorsHelper from '../../helpers/colours-helpers';
import WearDisplayHelpers from '../../helpers/wearDisplayHelpers';
import DateHelpers from '../../helpers/dateHelpers';
import { ObservableLevelsEnum } from '../../data/enums';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  idenifiers: string[];
  pathsLike: string[];
  entities: string[];
  dataLevel: ObservableLevelsEnum;
}

const InspectionStatusHistory: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  title = 'Status History',
  idenifiers = [],
  pathsLike = [],
  entities = [],
  dataLevel,
}) => {
  const { externalId } = useParams<{ externalId: string }>();
  const [mounted, setMounted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [, setHistoryParams] = useAtom(inspectionHistoryParamsAtom);
  const [{ data: items, isLoading }] = useAtom(inspectionHistoryAtom);

  const [isAnimatingIn, setIsAnimating] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  const slideOutAnimation = !isAnimatingOut
    ? 'translate-y-0'
    : 'translate-y-full';

  const slideAnimation = useMemo(
    () =>
      isAnimatingIn && isOpen
        ? 'translate-y-0'
        : isAnimatingOut
          ? slideOutAnimation
          : 'translate-y-full',
    [isAnimatingIn, isOpen, isAnimatingOut],
  );

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      setIsAnimating(true);
      setIsClosing(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (externalId && mounted) {
      setHistoryParams({
        externalId,
        identifiers: idenifiers,
        pathsLike: pathsLike,
        isOpen,
        entityExternalIds: entities,
        dataLevel,
      });
    }

    return () => {
      if (!isOpen) {
        setHistoryParams(null);
      }
    };
  }, [externalId, idenifiers, isOpen, setHistoryParams, mounted]);

  const handleClose = () => {
    setIsClosing(true);
    setIsAnimatingOut(true);
    onClose();
  };

  const handleTransitionEnd = useCallback(() => {
    if (isClosing) {
      if (isAnimatingIn) {
        setIsAnimating(false);
        setIsAnimatingOut(true);
        if (isAnimatingOut) {
          setIsAnimatingOut(false);
        }
      }
      setMounted(false);
      setIsClosing(false);
      setHistoryParams(null);
    }
  }, [isAnimatingIn, isAnimatingOut, isClosing]);

  if (!isOpen && !isAnimatingIn) return null;

  // if (!mounted) return null;

  if (isLoading && mounted && !isClosing) {
    return <ScreenSkeleton fields={4} />;
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50" onClick={handleClose}>
      <div
        className={`fixed right-0 bottom-0 left-0 transform rounded-t-xl bg-white transition-transform duration-500 ease-in-out ${
          slideAnimation
        }`}
        style={{
          transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)', // Custom easing function for a smoother long animation
        }}
        onClick={(e) => e.stopPropagation()}
        onTransitionEnd={handleTransitionEnd}
      >
        {/* Header */}
        <div className="relative flex items-center justify-center border-b p-4">
          <h2 className="text-lg font-bold">{title}</h2>
          <button
            onClick={handleClose}
            className="absolute right-4 rounded-full p-2 transition-colors hover:bg-brandLight/30"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[70vh] overflow-y-auto">
          {!items || items.length === 0 ? (
            <div className="p-4 text-center text-inkMuted">
              No history available
            </div>
          ) : (
            items.map((item) => (
              <div key={item.id} className="border-b p-4">
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center justify-center space-x-2">
                    <CircleUserRound size={16} />
                    <span className="font-bold">
                      {item.machineName} · {item.location}
                    </span>
                  </div>
                  <span className="text-sm font-bold">
                    {DateHelpers.toStringX(item.timestamp)}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex min-w-[48px] items-center justify-center rounded-full border border-line bg-white px-2 py-1 text-sm font-normal">
                    {WearDisplayHelpers.formatActionStatusPercentage(
                      item.actionStatus.percentage,
                    )}
                    %
                  </div>
                  <div className="relative h-6 flex-1 overflow-hidden rounded-full bg-gray-200">
                    <div
                      className="absolute h-full"
                      style={{
                        width: '100%',
                        backgroundColor:
                          item.actionStatus.actionType?.defaultColourCode,
                      }}
                    />
                    <div
                      className="absolute inset-0 flex items-center justify-center text-sm font-bold"
                      style={{
                        color: ColorsHelper.getContrastColor(
                          item.actionStatus.actionType?.defaultColourCode,
                        ),
                      }}
                    >
                      {item.actionStatus.actionType?.description}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default InspectionStatusHistory;
